export type OpportunityAutomationCustomer = {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  full_name?: string;
};

export type OpportunityAutomationData = {
  opportunity_id: number;
  business_id: string;
  customer_id: string;
  title: string;
  pipeline_id: number;
  stage_id: number;
  pipeline_name: string;
  stage_name: string;
  previous_stage_id?: number | null;
  previous_stage_name?: string | null;
  value: number;
  status: string;
  source?: string | null;
  business_name?: string | null;
  tags?: string | null;
  lead_info?: string | null;
  custom_fields?: Record<string, unknown> | string | null;
  created_at?: string;
  customer?: OpportunityAutomationCustomer;
};

export function parsePipelineStages(stages: unknown): Array<{ id: number; name: string }> {
  if (Array.isArray(stages)) return stages as Array<{ id: number; name: string }>;
  if (typeof stages === 'string') {
    try {
      const parsed = JSON.parse(stages);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function resolveStageName(
  stages: unknown,
  stageId: number | null | undefined
): string {
  if (stageId == null) return '';
  return parsePipelineStages(stages).find((s) => Number(s.id) === Number(stageId))?.name || '';
}

export function buildOpportunityAutomationData(input: {
  opportunity: Record<string, unknown>;
  customer?: Record<string, unknown> | null;
  pipelineName: string;
  stageName: string;
  previousStageId?: number | null;
  previousStageName?: string | null;
}): OpportunityAutomationData {
  const { opportunity, customer, pipelineName, stageName, previousStageId, previousStageName } =
    input;

  const firstName = customer?.first_name != null ? String(customer.first_name) : '';
  const lastName = customer?.last_name != null ? String(customer.last_name) : '';
  const fullName = `${firstName} ${lastName}`.trim();

  return {
    opportunity_id: Number(opportunity.id),
    business_id: String(opportunity.business_id),
    customer_id: String(opportunity.customer_id),
    title: String(opportunity.title ?? ''),
    pipeline_id: Number(opportunity.pipeline_id),
    stage_id: Number(opportunity.stage_id),
    pipeline_name: pipelineName,
    stage_name: stageName,
    previous_stage_id: previousStageId ?? null,
    previous_stage_name: previousStageName ?? null,
    value: Number(opportunity.value ?? 0),
    status: String(opportunity.status ?? 'Open'),
    source: opportunity.source != null ? String(opportunity.source) : null,
    business_name: opportunity.business_name != null ? String(opportunity.business_name) : null,
    tags: opportunity.tags != null ? String(opportunity.tags) : null,
    lead_info: opportunity.lead_info != null ? String(opportunity.lead_info) : null,
    custom_fields: (opportunity.custom_fields as OpportunityAutomationData['custom_fields']) ?? null,
    created_at: opportunity.created_at != null ? String(opportunity.created_at) : undefined,
    customer: customer
      ? {
          id: String(customer.id),
          first_name: firstName || undefined,
          last_name: lastName || undefined,
          email: customer.email != null ? String(customer.email) : undefined,
          phone: customer.phone != null ? String(customer.phone) : undefined,
          full_name: fullName || undefined,
        }
      : undefined,
  };
}

export const CRM_OPPORTUNITY_VARIABLE_DEFS: Array<{
  value: string;
  label: string;
  description: string;
}> = [
  { value: '{opportunity_id}', label: 'Opportunity ID', description: 'Numeric ID of the CRM opportunity' },
  { value: '{opportunity_title}', label: 'Opportunity Title', description: 'Title of the CRM opportunity' },
  { value: '{pipeline_name}', label: 'Pipeline Name', description: 'Name of the CRM pipeline' },
  { value: '{stage_name}', label: 'Stage Name', description: 'Name of the current CRM stage' },
  { value: '{old_stage_name}', label: 'Previous Stage Name', description: 'Stage the opportunity moved from (if any)' },
  { value: '{status}', label: 'Lead Status', description: 'Opportunity status (e.g. Open, Won, Lost)' },
  { value: '{value}', label: 'Deal Value', description: 'Opportunity value amount' },
  { value: '{source}', label: 'Lead Source', description: 'Where the lead came from' },
  { value: '{business_name}', label: 'Business Name', description: 'Business name on the opportunity' },
  { value: '{lead_info}', label: 'Lead Info', description: 'Lead info notes on the opportunity' },
  { value: '{tags}', label: 'Tags', description: 'Tags assigned to the opportunity' },
  { value: '{customer_name}', label: 'Customer Name', description: 'Full name of the linked customer' },
  { value: '{first_name}', label: 'First Name', description: 'Customer first name' },
  { value: '{last_name}', label: 'Last Name', description: 'Customer last name' },
  { value: '{customer_email}', label: 'Customer Email', description: 'Customer email address' },
  { value: '{customer_phone}', label: 'Customer Phone', description: 'Customer phone number' },
  { value: '{customer_id}', label: 'Customer ID', description: 'UUID of the linked customer' },
];
